<script lang="ts" setup>
import { ref } from "vue";

import useNotify from "@Composables/useNotify";
import { useTranslation } from "@Resources/js/composables";
import LinkSocial from "@Resources/js/components/LinkSocial.vue";
import Spinner from "@Resources/js/components/Spinner.vue";
import PopoverOverhauling from "@Resources/js/components/popover/PopoverOverhauling.vue";

/**
 * The teams counterpart of views/auth/EmailVerify.vue, rendered inside the overlay
 * instead of on its own route. Same markup and classes as the legacy panel so the
 * two look identical. Opens showing the check-your-email copy; a link is only sent
 * when the user asks for another.
 */

//
// Setup
const { __ } = useTranslation();
const { notifyError } = useNotify();

//
// Props
interface Props {
  resend: () => Promise<boolean>;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "close"): void;
}

const emit = defineEmits<Events>();

//
// Data
const isSending = ref(false);
const isLinkSent = ref(false);

//
// Methods
async function sendVerificationEmail(): Promise<void> {
  isSending.value = true;
  isLinkSent.value = false;

  const resent = await props.resend();

  isSending.value = false;

  if (resent) {
    isLinkSent.value = true;
  } else {
    notifyError({ text: __("something_went_wrong") });
  }
}

//
// Exposed Test Vars
defineExpose({ isSending, isLinkSent });
</script>

<template>
  <PopoverOverhauling @close="emit('close')">
    <div class="container-fluid py-3 _email_verify">
      <div class="mb-4 text-center">
        <h1 class="font-bungee font-weight-normal text-blue tracking-wide text-4xl">
          {{ __("overlay.email_verify.brand") }}
        </h1>
        <p class="text-sm text-capitalize">
          {{ __("email_verification") }}
        </p>
      </div>

      <div class="text-center">
        <Spinner v-if="isSending" />
        <h4
          v-if="isLinkSent"
          class="_sent"
        >
          {{ __("email_verification_sent") }}
        </h4>
        <p class="_check">
          {{ __("email_verification_check") }}
        </p>
        <p class="modal__body-txt">
          {{ __("if_not_receive_email") }},
          <a
            href="javascript:void(0);"
            class="_resend"
            @click.prevent="sendVerificationEmail"
          >{{ __("click_to_request") }}.</a>
        </p>
        <div class="modal__head-txt">
          {{ __("overlay.email_verify.connect_accounts") }}
        </div>
        <LinkSocial class="flex-center-center my-2 _social" />
      </div>
    </div>
  </PopoverOverhauling>
</template>
